import { ApiBaseUrl } from "@/redux/http";
import { logger } from "@/services/Logger";

const PRINT_BRIDGE_URL =
  (import.meta.env.VITE_PRINT_BRIDGE_URL as string | undefined) ||
  "http://localhost:9100/print";

export class PrintBridgeError extends Error {
  constructor(
    message: string,
    public readonly stage: "fetch-receipt" | "send-to-bridge",
    public readonly status?: number,
  ) {
    super(message);
    this.name = "PrintBridgeError";
  }
}

/**
 * Fetch the ESC/POS bytes for an order from the cloud and forward them to the
 * local print bridge running on the POS machine.
 *
 * Notes for callers:
 * - The SPA must be reachable from a context where mixed-content is allowed.
 *   The simplest setup: run this from the POS machine, where the bridge is at
 *   http://localhost:9100/print and modern browsers exempt localhost from
 *   HTTPS-only restrictions. Tablets calling a LAN IP from an HTTPS SPA will
 *   be blocked unless the bridge is served over HTTPS — keep printing on the
 *   POS machine for v1.
 * - VITE_PRINT_BRIDGE_URL overrides the default localhost URL when needed.
 */
export async function printOrderReceipt(orderUuid: string): Promise<void> {
  const receiptUrl = `${ApiBaseUrl}/api/orders/${orderUuid}/receipt?format=thermal`;

  let bytes: ArrayBuffer;
  try {
    const response = await fetch(receiptUrl, {
      method: "GET",
      credentials: "include",
    });
    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      throw new PrintBridgeError(
        `Receipt fetch failed: ${response.status} ${response.statusText}${detail ? ` — ${detail}` : ""}`,
        "fetch-receipt",
        response.status,
      );
    }
    bytes = await response.arrayBuffer();
  } catch (e) {
    if (e instanceof PrintBridgeError) throw e;
    logger.error("PrintBridgeService", "Failed to fetch receipt", e);
    throw new PrintBridgeError(
      `Receipt fetch error: ${e instanceof Error ? e.message : String(e)}`,
      "fetch-receipt",
    );
  }

  if (bytes.byteLength === 0) {
    throw new PrintBridgeError("Receipt was empty", "fetch-receipt");
  }

  try {
    const response = await fetch(PRINT_BRIDGE_URL, {
      method: "POST",
      headers: { "Content-Type": "application/octet-stream" },
      body: bytes,
    });
    if (!response.ok) {
      const detail = await response.text().catch(() => "");
      throw new PrintBridgeError(
        `Bridge rejected print: ${response.status} ${response.statusText}${detail ? ` — ${detail}` : ""}`,
        "send-to-bridge",
        response.status,
      );
    }
    logger.info("PrintBridgeService", `Printed ${bytes.byteLength} bytes`);
  } catch (e) {
    if (e instanceof PrintBridgeError) throw e;
    logger.error("PrintBridgeService", "Failed to reach print bridge", e);
    throw new PrintBridgeError(
      `Print bridge unreachable: ${e instanceof Error ? e.message : String(e)}`,
      "send-to-bridge",
    );
  }
}
